/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { CSRF_TOKEN } from '../../../modules/csrf/lib/constants.mjs'
import { getCSRFToken } from '../../../modules/csrf/lib/csrf-client'

const defaultHeaders: Record<string, string> = {}

const defaultOptions: RequestInit = {
  // Include cookies for same-origin request.
  credentials: 'same-origin'
}

/**
 * Returns parsed body depending on the "content-type" header.
 * @param resp
 */
export function handleResponseBody (resp: Response) {
  const contentType = resp.headers.get('content-type')

  if (contentType != null) {
    if (/json/gi.test(contentType)) {
      return resp.json()
    }
    if (contentType.indexOf('text/') === 0) {
      return resp.text()
    }
  }
  return resp.text()
}

/**
 * Throws an error if response is negative.
 * @param resp
 */
export async function handleResponseError (resp: Response) {
  if (!resp.ok) {
    const error: any = new Error(`${resp.status}: ${resp.statusText}`)
    error.response = resp
    error.body = await handleResponseBody(resp)

    if (error.body && error.body.error) {
      const bodyError = error.body.error

      if (bodyError.code != null) {
        error.code = bodyError.code
      }
      if (bodyError.message != null) {
        error.message = bodyError.message
      }
    }
    throw error
  }
  return resp
}

/**
 * Executes an HTTP request.
 * @param url
 * @param options
 */
function doFetch (url: string, options?: RequestInit) {
  const opts: RequestInit = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultHeaders,
      [CSRF_TOKEN]: getCSRFToken(),
      ...options?.headers
    }
  }
  return fetch(url, opts)
    .then(handleResponseError)
}

/**
 * Executes a DELETE request.
 * @param url
 * @param options
 */
function doDelete (url: string, options?: RequestInit) {
  return doFetch(url, { ...options, method: 'DELETE' })
}

/**
 * Executes a GET request.
 * @param url
 * @param options
 */
function doGet (url: string, options?: RequestInit) {
  return doFetch(url, { ...options, method: 'GET' })
}

/**
 * Executes a PATCH request.
 * @param url
 * @param options
 */
function doPatch (url: string, options?: RequestInit) {
  return doFetch(url, { ...options, method: 'PATCH' })
}

/**
 * Executes a POST request.
 * @param url
 * @param options
 */
function doPost (url: string, options?: RequestInit) {
  return doFetch(url, { ...options, method: 'POST' })
}

/**
 * Executes a PUT request.
 * @param url
 * @param options
 */
function doPut (url: string, options?: RequestInit) {
  return doFetch(url, { ...options, method: 'PUT' })
}

const rest = {
  delete: doDelete,
  fetch: doFetch,
  get: doGet,
  patch: doPatch,
  post: doPost,
  put: doPut
}

export default rest
